/**
 * Billing Settings Page
 *
 * Displays billing information, credit balance, and Stripe checkout.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { cn, formatCurrency, formatDate } from '@/utils';
import { Logger, LogCategory, logPaymentEvent } from '@/utils/logger';
import { AppLayout, PageHeader } from '@/components/layout';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { useCompany, useAuthStore } from '@/stores/authStore';
import { supabase } from '@/lib/supabase';
import { PRICING_PLANS, STRIPE_CONFIG } from '@/constants';
import type { BillingHistory, PricingPlan } from '@/types';
import {
  CreditCard,
  Check,
  Star,
  Clock,
  Download,
  ExternalLink,
  Zap,
} from 'lucide-react';

// Initialize Stripe (in production, use a valid key)
const stripePromise = loadStripe(STRIPE_CONFIG.PUBLISHABLE_KEY);

export function BillingSettingsPage() {
  const [searchParams] = useSearchParams();
  const company = useCompany();
  const { success: showSuccess, error: showError } = useToast();

  const initializeAuth = useAuthStore((state) => state.initializeAuth);

  const [billingHistory, setBillingHistory] = useState<BillingHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);

  // Handle success/cancel query params on mount
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true') {
      showSuccess('Subscription activated!', 'Your subscription has been successfully activated.');
      window.history.replaceState({}, '', '/settings/billing');
    } else if (canceled === 'true') {
      showError('Checkout canceled', 'Your checkout was canceled. No charges were made.');
      window.history.replaceState({}, '', '/settings/billing');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load billing history
  useEffect(() => {
    if (!company?.id) {
      setIsLoading(false);
      return;
    }

    Logger.info(LogCategory.UI, 'BillingSettingsPage mounted', 'BillingSettingsPage');

    const loadBillingHistory = async () => {
      try {
        const { data: history, error } = await supabase
          .from('billing_history')
          .select('*')
          .eq('company_id', company.id)
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) throw error;

        const transformedHistory: BillingHistory[] = (history || []).map((item: any) => ({
          id: item.id,
          date: item.created_at,
          description: item.description,
          amount: item.amount,
          credits: item.credits,
          status: item.status,
          paymentMethod: item.payment_method,
          invoiceUrl: item.invoice_url,
        }));

        setBillingHistory(transformedHistory);
        Logger.info(LogCategory.DATA, 'Billing history loaded', 'BillingSettingsPage', {
          count: transformedHistory.length,
        });
      } catch (error) {
        Logger.error(LogCategory.DATA, 'Failed to load billing history', 'BillingSettingsPage', undefined, error);
      } finally {
        setIsLoading(false);
      }
    };

    loadBillingHistory();
  }, [company?.id]);

  const handleSelectPlan = (plan: PricingPlan) => {
    Logger.info(LogCategory.PAYMENT, 'Plan selected', 'BillingSettingsPage', {
      planId: plan.id,
      price: plan.price,
      credits: plan.credits,
    });
    setSelectedPlan(plan);
  };

  const handlePurchase = async () => {
    if (!selectedPlan || !company) return;

    Logger.info(LogCategory.PAYMENT, 'Purchase initiated', 'BillingSettingsPage', {
      planId: selectedPlan.id,
      price: selectedPlan.price,
    });

    const planPrice = typeof selectedPlan.price === 'number' ? selectedPlan.price : 0;
    logPaymentEvent('checkout_started', planPrice, 'USD', {
      planId: selectedPlan.id,
      credits: selectedPlan.credits,
    });

    setIsProcessing(true);

    try {
      const stripe = await stripePromise;

      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Call Supabase Edge Function to create checkout session
      Logger.info(LogCategory.PAYMENT, 'Creating checkout session', 'BillingSettingsPage');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            priceId: selectedPlan.priceId,
            planTier: selectedPlan.tier,
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Checkout failed');
      if (!data?.url) throw new Error('No checkout URL returned');

      // Redirect to Stripe Checkout
      Logger.info(LogCategory.PAYMENT, 'Redirecting to Stripe Checkout', 'BillingSettingsPage');
      window.location.href = data.url;
    } catch (error) {
      Logger.error(LogCategory.PAYMENT, 'Purchase failed', 'BillingSettingsPage', undefined, error);
      logPaymentEvent('checkout_failed', planPrice, 'USD', undefined, error as Error);
      showError('Purchase failed', 'There was an error creating your checkout session. Please try again.');
      setIsProcessing(false);
    }
  };

  if (!company) {
    return null;
  }

  return (
    <AppLayout>
      <PageHeader
        title="Billing & Credits"
        description="Manage your subscription and purchase credits"
      />

      <div className="space-y-8">
        {/* Current Balance */}
        <Card variant="elevated" className="bg-gradient-to-r from-primary-600 to-primary-700">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-primary-100 text-sm font-medium">Available Credits</p>
                <p className="text-4xl font-bold text-white mt-1">
                  {company.credits.toLocaleString()}
                </p>
                <p className="text-primary-200 text-sm mt-2">
                  Current plan: <span className="capitalize font-medium">{company.plan}</span>
                </p>
              </div>
              <div className="h-16 w-16 bg-white/20 rounded-full flex items-center justify-center">
                <Zap className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Redeem Promo Code */}
        <Card>
          <CardHeader
            title="Redeem Promo Code"
            description="Enter a promotion code to receive credits"
          />
          <CardContent>
            <form
              className="flex items-end gap-3"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!promoCode.trim()) return;
                setIsRedeeming(true);
                try {
                  const { data, error } = await (supabase.rpc as any)('redeem_promotion', { p_code: promoCode.trim() });
                  if (error) throw error;
                  showSuccess('Promo code redeemed!', `${data.credits_added} credits have been added to your account.`);
                  setPromoCode('');
                  await initializeAuth();
                } catch (err: any) {
                  showError('Redemption failed', err?.message || 'Failed to redeem promo code.');
                } finally {
                  setIsRedeeming(false);
                }
              }}
            >
              <div className="flex-1">
                <Input
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                />
              </div>
              <Button type="submit" isLoading={isRedeeming} disabled={!promoCode.trim()}>
                Redeem
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Pricing Plans */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Subscription Plans</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {PRICING_PLANS.map((plan) => (
              <Card
                key={plan.id}
                className={cn(
                  'relative transition-all duration-200',
                  selectedPlan?.id === plan.id && 'ring-2 ring-primary-500',
                  plan.popular && 'border-primary-200'
                )}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge variant="info" size="sm">
                      <Star className="h-3 w-3 mr-1" />
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardContent className="pt-6">
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-semibold text-slate-900">{plan.name}</h3>
                    <p className="text-sm text-slate-500 mt-1">{plan.description}</p>
                    <div className="mt-4">
                      <span className="text-4xl font-bold text-slate-900">
                        {plan.price === 'custom' ? 'Custom' : formatCurrency(plan.price)}
                      </span>
                      {plan.price !== 'custom' && plan.price > 0 && (
                        <span className="text-sm text-slate-500">/mo</span>
                      )}
                    </div>
                    {plan.credits !== 'custom' && (
                      <p className="text-sm text-primary-600 font-medium mt-2">
                        {plan.credits.toLocaleString()} checks included
                      </p>
                    )}
                    {plan.overageRate && (
                      <p className="text-xs text-slate-500 mt-1">
                        {formatCurrency(plan.overageRate)} per overage
                      </p>
                    )}
                  </div>

                  <div className="space-y-4 mb-6">
                    {plan.features.length > 0 && (
                      <ul className="space-y-2">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <Check className="h-4 w-4 text-success-500 shrink-0 mt-0.5" />
                            <span className="text-xs text-slate-600">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {plan.extras && plan.extras.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-700 mb-2">Extras:</p>
                        <ul className="space-y-1">
                          {plan.extras.map((extra, index) => (
                            <li key={index} className="text-xs text-slate-600">
                              • {extra}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </CardContent>
                <CardFooter align="center">
                  <Button
                    variant={selectedPlan?.id === plan.id ? 'primary' : 'outline'}
                    fullWidth
                    size="sm"
                    onClick={() => handleSelectPlan(plan)}
                    disabled={plan.tier === 'enterprise'}
                  >
                    {plan.tier === 'enterprise'
                      ? 'Contact Sales'
                      : selectedPlan?.id === plan.id
                      ? 'Selected'
                      : 'Select'}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Purchase Button */}
          {selectedPlan && selectedPlan.tier !== 'enterprise' && (
            <div className="mt-6 p-4 bg-slate-50 rounded-lg flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">
                  {selectedPlan.name}
                  {selectedPlan.credits !== 'custom' && ` - ${selectedPlan.credits.toLocaleString()} checks`}
                </p>
                <p className="text-sm text-slate-500">
                  {selectedPlan.price === 'custom'
                    ? 'Contact sales for pricing'
                    : `${formatCurrency(selectedPlan.price)}/month`}
                  {selectedPlan.overageRate &&
                    ` + ${formatCurrency(selectedPlan.overageRate)} per additional check`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={() => setSelectedPlan(null)}>
                  Cancel
                </Button>
                <Button
                  leftIcon={<CreditCard className="h-4 w-4" />}
                  onClick={handlePurchase}
                  isLoading={isProcessing}
                >
                  {selectedPlan.tier === 'trial' ? 'Start Trial' : 'Subscribe'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Billing History */}
        <Card>
          <CardHeader
            title="Billing History"
            description="Your past transactions and invoices"
          />
          <CardContent padding="none">
            {isLoading ? (
              <div className="p-6 space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between animate-pulse">
                    <div>
                      <div className="h-4 bg-slate-200 rounded w-48 mb-2" />
                      <div className="h-3 bg-slate-200 rounded w-24" />
                    </div>
                    <div className="h-6 bg-slate-200 rounded w-16" />
                  </div>
                ))}
              </div>
            ) : billingHistory.length === 0 ? (
              <div className="p-8 text-center">
                <Clock className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                <p className="text-slate-600">No billing history yet.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {billingHistory.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-4">
                    <div>
                      <p className="font-medium text-slate-900">{item.description}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm text-slate-500">
                          {formatDate(item.date, 'short')}
                        </span>
                        {item.paymentMethod && (
                          <>
                            <span className="text-slate-300">•</span>
                            <span className="text-sm text-slate-500">{item.paymentMethod}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-medium text-slate-900">
                          {formatCurrency(item.amount)}
                        </p>
                        <Badge
                          variant={
                            item.status === 'completed'
                              ? 'success'
                              : item.status === 'pending'
                              ? 'warning'
                              : 'danger'
                          }
                          size="sm"
                        >
                          {item.status}
                        </Badge>
                      </div>
                      {item.invoiceUrl && (
                        <a href={item.invoiceUrl} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription Management */}
        {company.stripeCustomerId && (
          <Card>
            <CardHeader
              title="Subscription Management"
              description="Manage your subscription, payment methods, and billing details"
            />
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">
                Access the Stripe Customer Portal to update your payment method, view invoices, or cancel your subscription.
              </p>
              <Button
                variant="outline"
                leftIcon={<ExternalLink className="h-4 w-4" />}
                onClick={async () => {
                  try {
                    const { data, error } = await supabase.functions.invoke('create-portal-session');
                    if (error) throw error;
                    if (data?.url) window.location.href = data.url;
                  } catch (error) {
                    Logger.error(LogCategory.PAYMENT, 'Failed to open portal', 'BillingSettingsPage', undefined, error);
                    showError('Error', 'Failed to open customer portal. Please try again.');
                  }
                }}
              >
                Manage Subscription
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
